import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'pageRow',
    'checkbox',
    'selectAll',
    'bulkActions',
    'selectedCount',
    'deleteButton'
  ]

  static values = {
    websiteId: String,
    deleteUrl: String,
    bulkDeleteUrl: String
  }

  connect() {
    this.updateBulkActionsVisibility()
  }

  // Toggle all checkboxes
  toggleSelectAll(event) {
    const isChecked = event.target.checked
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = isChecked
    })
    this.updateBulkActionsVisibility()
  }

  // Handle individual checkbox change
  checkboxChanged() {
    this.updateBulkActionsVisibility()
    this.updateSelectAllState()
  }

  // Update select all checkbox state based on individual checkboxes
  updateSelectAllState() {
    if (!this.hasSelectAllTarget) return

    const checkedCount = this.getSelectedPageIds().length
    const totalCount = this.checkboxTargets.length

    if (checkedCount === 0) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    } else if (checkedCount === totalCount) {
      this.selectAllTarget.checked = true
      this.selectAllTarget.indeterminate = false
    } else {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = true
    }
  }

  // Show/hide bulk actions based on selection
  updateBulkActionsVisibility() {
    const selectedIds = this.getSelectedPageIds()
    const hasSelection = selectedIds.length > 0

    if (this.hasBulkActionsTarget) {
      if (hasSelection) {
        this.bulkActionsTarget.classList.remove('hidden')
      } else {
        this.bulkActionsTarget.classList.add('hidden')
      }
    }

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = selectedIds.length
    }
  }

  // Get array of selected page IDs
  getSelectedPageIds() {
    return this.checkboxTargets
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.dataset.pageId)
  }

  // Delete a single page via AJAX
  async deletePage(event) {
    event.preventDefault()

    const button = event.currentTarget
    const pageId = button.dataset.pageId
    const pageTitle = button.dataset.pageTitle
    const row = button.closest('[data-admin--page-manager-target="pageRow"]')

    // Show confirmation
    const confirmed = await this.showConfirmation(
      `Delete "${pageTitle}"?`,
      'This page will be removed. It can be regenerated later.'
    )

    if (!confirmed) return

    // Disable button and show loading
    button.disabled = true
    button.classList.add('opacity-50')

    try {
      const response = await fetch(`/admin/websites/${this.websiteIdValue}/pages/${pageId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        // Animate row removal
        this.removeRowWithAnimation(row)
        this.showToast(`"${pageTitle}" deleted`, 'success')
      } else {
        const data = await response.json().catch(() => ({}))
        this.showToast(data.error || 'Failed to delete page', 'error')
        button.disabled = false
        button.classList.remove('opacity-50')
      }
    } catch (error) {
      console.error('[PageManager] Delete error:', error)
      this.showToast('Network error occurred', 'error')
      button.disabled = false
      button.classList.remove('opacity-50')
    }
  }

  // Bulk delete selected pages
  async deleteSelected() {
    const selectedIds = this.getSelectedPageIds()

    if (selectedIds.length === 0) {
      this.showToast('No pages selected', 'warning')
      return
    }

    // Get titles for confirmation
    const selectedTitles = this.checkboxTargets
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.dataset.pageTitle)
      .slice(0, 3)
      .join(', ')

    const moreCount = selectedIds.length - 3
    const titleList = moreCount > 0 ?
      `${selectedTitles}, and ${moreCount} more` :
      selectedTitles

    // Show confirmation
    const confirmed = await this.showConfirmation(
      `Delete ${selectedIds.length} page${selectedIds.length > 1 ? 's' : ''}?`,
      `You are about to delete: ${titleList}. These pages can be regenerated later.`
    )

    if (!confirmed) return

    // Disable bulk actions during deletion
    if (this.hasBulkActionsTarget) {
      this.bulkActionsTarget.classList.add('opacity-50', 'pointer-events-none')
    }

    try {
      const response = await fetch(`/admin/websites/${this.websiteIdValue}/pages/bulk_destroy`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': this.csrfToken,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ page_ids: selectedIds })
      })

      if (response.ok) {
        const data = await response.json()

        // Remove each deleted row
        selectedIds.forEach((pageId) => {
          const row = this.element.querySelector(`[data-page-id="${pageId}"]`)?.closest('[data-admin--page-manager-target="pageRow"]')
          if (row) {
            this.removeRowWithAnimation(row)
          }
        })

        // Uncheck select all
        if (this.hasSelectAllTarget) {
          this.selectAllTarget.checked = false
          this.selectAllTarget.indeterminate = false
        }

        this.showToast(`${data.deleted_count} page${data.deleted_count > 1 ? 's' : ''} deleted`, 'success')
      } else {
        const data = await response.json().catch(() => ({}))
        this.showToast(data.error || 'Failed to delete pages', 'error')
      }
    } catch (error) {
      console.error('[PageManager] Bulk delete error:', error)
      this.showToast('Network error occurred', 'error')
    } finally {
      if (this.hasBulkActionsTarget) {
        this.bulkActionsTarget.classList.remove('opacity-50', 'pointer-events-none')
      }
      this.updateBulkActionsVisibility()
    }
  }

  // Animate row removal
  removeRowWithAnimation(row) {
    row.style.transition = 'all 0.3s ease-out'
    row.style.opacity = '0'
    row.style.transform = 'translateX(-20px)'

    setTimeout(() => {
      row.remove()
      this.updateBulkActionsVisibility()
    }, 300)
  }

  // Show custom confirmation dialog
  showConfirmation(title, message) {
    return new Promise((resolve) => {
      // Create modal backdrop
      const backdrop = document.createElement('div')
      backdrop.className = 'fixed inset-0 bg-black/50 flex items-center justify-center z-50'
      backdrop.style.animation = 'fadeIn 0.15s ease-out'

      // Create modal
      const modal = document.createElement('div')
      modal.className = 'bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden'
      modal.style.animation = 'slideUp 0.2s ease-out'
      modal.innerHTML = `
        <div class="p-6">
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style="background-color: var(--color-warning-100);">
              <svg class="w-5 h-5" style="color: var(--color-warning-600);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <div class="flex-1">
              <h3 class="font-semibold" style="color: var(--text-primary); font-size: var(--text-lg);">${title}</h3>
              <p class="mt-2 text-sm" style="color: var(--text-secondary);">${message}</p>
            </div>
          </div>
        </div>
        <div class="flex justify-end gap-3 px-6 py-4" style="background-color: var(--bg-muted); border-top: 1px solid var(--border-muted);">
          <button type="button" data-action="cancel" class="btn btn-secondary">Cancel</button>
          <button type="button" data-action="confirm" class="btn" style="background-color: var(--color-error-600); color: white;">Delete</button>
        </div>
      `

      backdrop.appendChild(modal)
      document.body.appendChild(backdrop)

      // Add animation styles if not present
      if (!document.getElementById('confirmation-modal-styles')) {
        const style = document.createElement('style')
        style.id = 'confirmation-modal-styles'
        style.textContent = `
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideUp {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `
        document.head.appendChild(style)
      }

      // Handle clicks
      const cleanup = (result) => {
        backdrop.style.animation = 'fadeIn 0.15s ease-out reverse'
        modal.style.animation = 'slideUp 0.15s ease-out reverse'
        setTimeout(() => {
          backdrop.remove()
          resolve(result)
        }, 150)
      }

      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) cleanup(false)
      })

      modal.querySelector('[data-action="cancel"]').addEventListener('click', () => cleanup(false))
      modal.querySelector('[data-action="confirm"]').addEventListener('click', () => cleanup(true))

      // Handle escape key
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          document.removeEventListener('keydown', handleEscape)
          cleanup(false)
        }
      }
      document.addEventListener('keydown', handleEscape)
    })
  }

  // Show toast notification
  showToast(message, type = 'info') {
    // Remove any existing toasts
    document.querySelectorAll('.page-manager-toast').forEach((el) => el.remove())

    const toast = document.createElement('div')
    toast.className = 'page-manager-toast fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50'
    toast.style.animation = 'slideUp 0.2s ease-out'

    const colors = {
      success: { bg: 'var(--color-success-50)', border: 'var(--color-success-200)', text: 'var(--color-success-800)', icon: 'var(--color-success-500)' },
      error: { bg: 'var(--color-error-50)', border: 'var(--color-error-200)', text: 'var(--color-error-800)', icon: 'var(--color-error-500)' },
      warning: { bg: 'var(--color-warning-50)', border: 'var(--color-warning-200)', text: 'var(--color-warning-800)', icon: 'var(--color-warning-500)' },
      info: { bg: 'var(--bg-muted)', border: 'var(--border-muted)', text: 'var(--text-primary)', icon: 'var(--color-primary-500)' }
    }

    const c = colors[type] || colors.info
    toast.style.backgroundColor = c.bg
    toast.style.border = `1px solid ${c.border}`
    toast.style.color = c.text

    const iconPath = type === 'success' ?
      'M5 13l4 4L19 7' :
      type === 'error' ?
        'M6 18L18 6M6 6l12 12' :
        'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'

    toast.innerHTML = `
      <svg class="w-5 h-5 flex-shrink-0" style="color: ${c.icon};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="${iconPath}"/>
      </svg>
      <span class="font-medium">${message}</span>
    `

    document.body.appendChild(toast)

    // Auto-remove after 3 seconds
    setTimeout(() => {
      toast.style.animation = 'slideUp 0.2s ease-out reverse'
      setTimeout(() => toast.remove(), 200)
    }, 3000)
  }

  // Get CSRF token
  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
}
